import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';
import dayjs from 'dayjs';
import { instance } from '~/utils/api/api';
import { Spinner } from '~/ui';
import { getLocalTimeDate } from '~/utils/getLocalTimeDate';
import styles from './TicketList.module.scss';

interface TicketListItem {
  id: number;
  name: string;
  description: string;
  review: string | null;
  created_at: string;
  ticket_photo: unknown[];
  ticket_video: unknown[];
  category: { id: number; category: string } | null;
  statuses: { id: number; name: string } | null;
  comments_count: number;
  last_comment: {
    id: number;
    description: string;
    created_at: string;
    user_id: number;
    my_comment: boolean;
    time: string;
  } | null;
}

interface TicketListProps {
  selectedTicketId: number | null;
  onSelect: (ticketId: number | null) => void;
}

export const TicketList = ({ selectedTicketId, onSelect }: TicketListProps) => {
  const { userID } = useParams();
  const { t } = useTranslation();

  const { data, isLoading } = useQuery({
    queryFn: async () => {
      const response = await instance.get(
        `admin/users/${userID}/chat/tickets`
      );
      return response.data;
    },
    queryKey: ['user-chat-tickets', userID],
  });

  const tickets: TicketListItem[] = data?.tickets ?? [];

  if (isLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.loader}>
          <Spinner />
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* "Все заявки" */}
      <div
        className={cn(styles.allTicketsItem, {
          [styles.active]: selectedTicketId === null,
        })}
        onClick={() => onSelect(null)}
      >
        {t('all_requests')}
      </div>

      {tickets.length === 0 && (
        <div className={styles.empty}>{t('no_requests')}</div>
      )}

      {tickets.map(ticket => (
        <div
          key={ticket.id}
          className={cn(styles.item, {
            [styles.active]: selectedTicketId === ticket.id,
          })}
          onClick={() => onSelect(ticket.id)}
        >
          <div className={styles.ticketName}>
            #{ticket.id} {t('request_dated')} {dayjs(ticket.created_at).format('DD.MM.YYYY')}
          </div>
          {ticket.category && (
            <div className={styles.category}>{ticket.category.category}</div>
          )}
          {ticket.last_comment ? (
            <div className={styles.lastMessage}>
              {ticket.last_comment.description?.slice(0, 50) || '📷 Фото'}
            </div>
          ) : (
            <div className={styles.lastMessage}>
              {ticket.description?.slice(0, 50) || '—'}
            </div>
          )}
          <div className={styles.meta}>
            <span className={styles.date}>
              {getLocalTimeDate(
                ticket.last_comment?.created_at || ticket.created_at
              )}
            </span>
            {ticket.comments_count > 0 && (
              <span className={styles.commentsCount}>
                {ticket.comments_count}
              </span>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};
